import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { MantineProvider } from "@mantine/core";
import { useOutlet } from "react-router-dom";

import { AppPage } from "../../components/AppPage";
import { Pagination } from "../../components/Pagination";
import { RpiTableContainer } from "../../components/RpiTableContainer";
import { RpiSearchFilter } from "../../components/search-filter/RpiSearchFilter";
import { SearchFilterBar } from "../../components/search-filter/SearchFilterBar";
import { printAssetColumns } from "../../configuration/columns/printAsset.column";
import { usePrintAssetFilters } from "../../configuration/filters/printAsset.filter";
import { SearchInput, toSearchInputArray } from "../../configuration/filters/searchInput";
import { featureFlags } from "../../featureFlags";
import { useRpiPagination } from "../../hooks/usePagination";
import { RpiTable } from "../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../utils/graphql.util";
import { printAssetSearchLabels, printAssetSearchRenderers } from "../../utils/searchField.util";
import { PrintAssetsFilter } from "./PrintAssetsFilter";

export const PrintAssetsView: React.FC = () => {
  const outlet = useOutlet();
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.PrintAssetsQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.PrintAssetSearchFields>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.PrintAssetsQueryVariables>>();

  const searchFilters = toSearchInputArray(searchInputs);
  const setSearchFilters = (
    recipe: (filters: SearchInput<GraphQL.PrintAssetSearchFields>[]) => SearchInput<GraphQL.PrintAssetSearchFields>[]
  ) => {
    setSearchInputs((prev) => recipe(toSearchInputArray(prev)));
  };

  const pagination = useRpiPagination({ perPage: 20 });

  const printAssetFilters = usePrintAssetFilters();

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error, refetch } = GraphQL.usePrintAssetsQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.printAssets.totalCount),
    }
  );
  const printAssets = React.useMemo(() => data?.printAssets.data, [data]);

  return (
    <AppPage path={["Print Assets"]} onReload={refetch}>
      {outlet}

      <AppPage.Section>
        {featureFlags.newSearchExperience ? (
          <SearchFilterBar
            placeholder="Search by Upload ID"
            isLoading={isLoading}
            filters={searchFilters}
            onSetFilters={setSearchFilters}
            onSearchSubmit={handleSearchSubmit}
            onFilterClick={() => setIsFilterOpen(true)}
            filterLabels={printAssetSearchLabels}
            valueRenderers={printAssetSearchRenderers}
          />
        ) : (
          <RpiSearchFilter
            placeholder="Search by Upload ID"
            filters={printAssetFilters}
            dateRangeField={GraphQL.PrintAssetSearchFields.UpdatedAt}
            onChange={setSearchInputs}
            onSearch={setSearchGeneral}
          />
        )}

        <MantineProvider theme={rpaTheme}>
          <RpiTableContainer
            data-testid="print-assets-table"
            footer={<Pagination isLoading={isLoading} pagination={pagination} label="Files" />}
          >
            <RpiTable
              emptyMessage="No print assets found."
              isLoading={isLoading}
              error={error}
              perPage={printAssets?.length}
              data={printAssets}
              columns={printAssetColumns}
              withSortBy={{ onChange: setSortInput }}
            />
          </RpiTableContainer>
        </MantineProvider>
      </AppPage.Section>
      {featureFlags.newSearchExperience && (
        <PrintAssetsFilter
          isOpened={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={searchFilters}
          onApply={setSearchInputs}
        />
      )}
    </AppPage>
  );
};
